import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Search,
  Users,
  Filter,
  LayoutGrid,
  List,
  ChevronRight,
  Mail,
  Calendar,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
const STAGES = [
  { value: 'all', label: 'All Stages' },
  { value: 'registered', label: 'Registered' },
  { value: 'fear_started', label: 'EDGE Started' },
  { value: 'fear_complete', label: 'EDGE Complete' },
  { value: 'archetype_started', label: 'CORE Started' },
  { value: 'archetype_complete', label: 'CORE Complete' },
  { value: 'booked', label: 'Booked Session' },
  { value: 'applied', label: 'Applied' },
  { value: 'accepted', label: 'Accepted' },
];

const STAGE_COLORS = {
  registered: 'bg-gray-100 text-gray-800',
  fear_started: 'bg-amber-100 text-amber-800',
  fear_complete: 'bg-amber-200 text-amber-900',
  archetype_started: 'bg-violet-100 text-violet-800',
  archetype_complete: 'bg-violet-200 text-violet-900',
  booked: 'bg-green-100 text-green-800',
  applied: 'bg-blue-100 text-blue-800',
  accepted: 'bg-emerald-100 text-emerald-800',
};

const STAGE_LABELS = {
  registered: 'Registered',
  fear_started: 'EDGE Started',
  fear_complete: 'EDGE Complete',
  archetype_started: 'CORE Started',
  archetype_complete: 'CORE Complete',
  booked: 'Booked',
  applied: 'Applied',
  accepted: 'Accepted',
};

export default function AdminParticipants() {
  const [participants, setParticipants] = useState([]);
  const [stageData, setStageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' or 'kanban'
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    fetchData();
  }, [stageFilter, searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      if (view === 'kanban') {
        const res = await axios.get('/api/admin/participants/stages');
        setStageData(res.data);
      } else {
        const params = {};
        if (stageFilter !== 'all') params.stage = stageFilter;
        if (searchQuery) params.search = searchQuery;
        
        const res = await axios.get('/api/admin/participants', { params });
        setParticipants(res.data.participants || []);
      }
    } catch (error) {
      toast.error('Failed to load participants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [view]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(participants.map(p => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) {
      toast.error('No participants selected');
      return;
    }
    toast.info(`Bulk action "${action}" would be applied to ${selectedIds.length} participants`);
    // Implement bulk actions as needed
  };

  return (
      <div data-testid="admin-participants">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold">Participants</h1>
            <p className="text-muted-foreground">Manage and track participant progress</p>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* View Toggle and Filters */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
          <Tabs value={view} onValueChange={setView} className="w-auto">
            <TabsList>
              <TabsTrigger value="list" className="flex items-center gap-2">
                <List className="w-4 h-4" /> List
              </TabsTrigger>
              <TabsTrigger value="kanban" className="flex items-center gap-2">
                <LayoutGrid className="w-4 h-4" /> Pipeline
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="flex-1" />
          
          {view === 'list' && (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-64"
                  data-testid="search-input"
                />
              </div>
              
              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger className="w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
        </div>

        {/* Bulk Actions */}
        {view === 'list' && selectedIds.length > 0 && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4 flex items-center gap-4">
            <span className="text-sm font-medium">{selectedIds.length} selected</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleBulkAction('email')}>
                <Mail className="w-4 h-4 mr-1" /> Send Email
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction('export')}>
                Export
              </Button>
            </div>
            <button 
              onClick={() => setSelectedIds([])} 
              className="ml-auto text-sm text-muted-foreground hover:text-foreground"
            >
              Clear selection
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          </div>
        ) : view === 'list' ? (
          /* List View */
          <div className="bg-card border border-border rounded-[var(--radius)] overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="w-12 p-4">
                    <input 
                      type="checkbox" 
                      checked={selectedIds.length === participants.length && participants.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="text-left p-4 text-sm font-medium">Participant</th>
                  <th className="text-left p-4 text-sm font-medium">Stage</th>
                  <th className="text-left p-4 text-sm font-medium">Primary Edge</th>
                  <th className="text-left p-4 text-sm font-medium">Core Archetype</th>
                  <th className="text-left p-4 text-sm font-medium">Registered</th>
                  <th className="text-left p-4 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {participants.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-muted-foreground">
                      No participants found
                    </td>
                  </tr>
                ) : (
                  participants.map((p) => (
                    <tr 
                      key={p.id} 
                      className="border-t border-border hover:bg-muted/30 cursor-pointer"
                      onClick={() => window.location.href = `/admin/participants/${p.id}`}
                    >
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <input 
                          type="checkbox" 
                          checked={selectedIds.includes(p.id)}
                          onChange={() => handleSelectOne(p.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium">
                            {p.firstName || p.lastName 
                              ? `${p.firstName} ${p.lastName}`.trim() 
                              : p.email.split('@')[0]}
                          </p>
                          <p className="text-sm text-muted-foreground">{p.email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${STAGE_COLORS[p.stage] || 'bg-gray-100'}`}>
                          {STAGE_LABELS[p.stage] || p.stage}
                        </span>
                      </td>
                      <td className="p-4">
                        {p.fearCluster ? (
                          <span className="text-sm">{p.fearCluster}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        {p.archetype ? (
                          <span className="text-sm">{p.archetype}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {formatDate(p.createdAt)}
                      </td>
                      <td className="p-4">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = `/admin/participants/${p.id}`;
                          }}
                        >
                          View <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          /* Kanban/Pipeline View */
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {Object.entries(STAGE_LABELS).map(([stage, label]) => (
                <div 
                  key={stage} 
                  className="w-64 flex-shrink-0 bg-muted/30 rounded-lg overflow-hidden"
                >
                  <div className={`p-3 ${STAGE_COLORS[stage]} border-b`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{label}</span>
                      <span className="text-xs bg-white/50 px-2 py-0.5 rounded">
                        {stageData?.counts?.[stage] || 0}
                      </span>
                    </div>
                  </div>
                  <div className="p-2 space-y-2 max-h-[60vh] overflow-y-auto">
                    {stageData?.stages?.[stage]?.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-4">No participants</p>
                    ) : (
                      stageData?.stages?.[stage]?.map((p) => (
                        <div 
                          key={p.id}
                          onClick={() => window.location.href = `/admin/participants/${p.id}`}
                          className="bg-card border border-border rounded p-3 cursor-pointer hover:shadow-md transition-shadow"
                        >
                          <p className="font-medium text-sm truncate">{p.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{p.email}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
  );
}
